import { useEffect, useState } from "react";
import { Search, X } from "lucide-react";
import { toast } from "sonner";
import { api, CAP_LABELS, fmtErr } from "@/lib/api";

const fmtD = (iso) => (iso ? new Date(iso).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" }) : "—");

export default function ClientsTab() {
  const [clients, setClients] = useState([]);
  const [q, setQ] = useState("");
  const [age, setAge] = useState("");
  const [profile, setProfile] = useState(null);
  const [err, setErr] = useState("");

  const load = async () => {
    try {
      setErr("");
      const { data } = await api.get("/coach/clients", { params: { q, age_range: age } });
      setClients(data);
    } catch (e) {
      setErr(fmtErr(e));
    }
  };
  useEffect(() => { const t = setTimeout(load, 300); return () => clearTimeout(t); }, [q, age]);

  const open = async (id) => {
    try {
      const { data } = await api.get(`/coach/clients/${id}`);
      setProfile(data);
    } catch (e) { toast.error(fmtErr(e)); }
  };

  return (
    <div data-testid="coach-clients">
      <h2 className="font-display uppercase text-4xl mb-6">Clients <span className="text-zinc-600">({clients.length})</span></h2>
      <div className="flex flex-wrap gap-2 mb-5">
        <div className="flex items-center gap-2 bg-zinc-950 border border-zinc-700 px-3 flex-1 min-w-[240px]">
          <Search size={15} className="text-zinc-500" />
          <input data-testid="clients-search" value={q} onChange={(e) => setQ(e.target.value)}
            placeholder="Search name, Client ID, email or phone…"
            className="w-full bg-transparent py-3 text-sm focus:outline-none placeholder-zinc-600" />
        </div>
        <select data-testid="clients-age-filter" value={age} onChange={(e) => setAge(e.target.value)}
          className="bg-zinc-950 border border-zinc-700 px-3 py-3 text-sm text-zinc-300 focus:border-volt outline-none">
          <option value="">All ages</option>
          {["18-29", "30-44", "45-59", "60+"].map((a) => <option key={a} value={a}>{a}</option>)}
        </select>
      </div>
      {err && <p className="text-red-400 text-sm mb-4">{err}</p>}

      <div className="border border-zinc-800 divide-y divide-zinc-800/70">
        {clients.map((c) => (
          <button key={c.id} data-testid={`client-row-${c.client_code}`} onClick={() => open(c.id)}
            className="w-full text-left bg-zinc-950 p-4 flex flex-wrap items-center justify-between gap-3 hover:bg-zinc-900 transition-colors">
            <div>
              <p className="font-bold text-zinc-100">{c.full_name} <span className="text-volt font-display tracking-widest ml-2">{c.client_code}</span></p>
              <p className="text-xs text-zinc-500">{c.email}{c.phone ? ` · ${c.phone}` : ""}{c.age_range ? ` · ${c.age_range}` : ""}</p>
            </div>
            <div className="text-right">
              {c.latest_score != null
                ? <p className="font-display text-2xl text-volt">{c.latest_score}<span className="text-xs text-zinc-500">/100</span></p>
                : <p className="text-xs text-zinc-600">No assessment</p>}
              <p className="text-[10px] uppercase tracking-[0.14em] text-zinc-500">{c.assessment_count} assessment(s)</p>
            </div>
          </button>
        ))}
        {!clients.length && !err && <p className="p-5 text-sm text-zinc-600">No clients found.</p>}
      </div>

      {profile && (
        <div className="fixed inset-0 z-[75] bg-ink/85 backdrop-blur-sm flex justify-end" onClick={() => setProfile(null)}>
          <div data-testid="client-profile-drawer" onClick={(e) => e.stopPropagation()}
            className="w-full max-w-xl h-full bg-zinc-950 border-l border-zinc-800 overflow-y-auto p-6 sm:p-8"
            style={{ overscrollBehavior: "contain" }}>
            <div className="flex items-start justify-between">
              <div>
                <p className="text-[10px] uppercase tracking-[0.25em] text-zinc-500 font-bold">Client overview</p>
                <h3 className="font-display uppercase text-4xl mt-1">{profile.client.full_name}</h3>
                <p className="text-volt font-display tracking-widest text-lg">{profile.client.client_code}</p>
                <p className="text-xs text-zinc-500 mt-1">
                  {profile.client.email}{profile.client.phone ? ` · ${profile.client.phone}` : ""}{profile.client.age_range ? ` · ${profile.client.age_range}` : ""}
                </p>
              </div>
              <button onClick={() => setProfile(null)} data-testid="client-profile-close" className="text-zinc-500 hover:text-volt"><X size={20} /></button>
            </div>

            {profile.score_progress && (
              <div className="mt-6 border border-volt/40 bg-volt/5 p-5">
                <p className="text-[10px] uppercase tracking-[0.2em] text-zinc-400 font-bold">Score progress</p>
                <p className="font-display text-4xl mt-1">
                  {profile.score_progress.first} <span className="text-zinc-500">→</span> <span className="text-volt">{profile.score_progress.latest}</span>
                  <span className="text-lg text-volt ml-3">+{profile.score_progress.change} ({profile.score_progress.pct}%)</span>
                </p>
              </div>
            )}

            <Section title="Assessment history" count={profile.assessments.length}>
              {profile.assessments.map((a) => (
                <div key={a.id} className="flex justify-between text-sm border-b border-zinc-800/60 py-2">
                  <span className="text-zinc-400">{fmtD(a.completed_at)} · {a.assessment_version}</span>
                  <span className="text-zinc-200">
                    <span className="font-display text-lg text-volt">{a.overall_score}</span>
                    <span className="text-xs text-zinc-500"> · strong {CAP_LABELS[a.strongest_area] || a.strongest_area} · dev {CAP_LABELS[a.development_area] || a.development_area}</span>
                  </span>
                </div>
              ))}
            </Section>

            <Section title="Sessions" count={profile.registrations.length}>
              {profile.registrations.map((r) => (
                <div key={r.id} className="flex justify-between text-sm border-b border-zinc-800/60 py-2">
                  <span className="text-zinc-300">{r.group_sessions?.session_name} · {r.group_sessions?.date}</span>
                  <span className={`text-[10px] uppercase tracking-[0.12em] font-bold ${r.registration_status === "confirmed" ? "text-volt" : "text-zinc-500"}`}>
                    {r.registration_status}
                  </span>
                </div>
              ))}
              {profile.attendance.length > 0 && (
                <p className="text-xs text-zinc-500 pt-2">
                  Attendance: {profile.attendance.filter((a) => a.status === "attended").length} attended / {profile.attendance.length} recorded
                </p>
              )}
            </Section>

            <Section title="Training" count={profile.assignments.length + profile.logs.length}>
              {profile.assignments.map((a) => (
                <div key={a.id} className="flex justify-between text-sm border-b border-zinc-800/60 py-2">
                  <span className="text-zinc-300">{a.workout_name}</span>
                  <span className="text-xs text-zinc-500">{a.scheduled_date} · {a.status}</span>
                </div>
              ))}
              {profile.logs.map((l) => (
                <div key={l.id} className="flex justify-between text-sm border-b border-zinc-800/60 py-2">
                  <span className="text-zinc-300">Workout completed</span>
                  <span className="text-xs text-zinc-500">{fmtD(l.completed_at)}{l.duration ? ` · ${l.duration}m` : ""} · {l.completion_status}</span>
                </div>
              ))}
            </Section>

            <Section title="Feedback" count={profile.feedback.length}>
              {profile.feedback.map((f) => (
                <div key={f.id} className="text-sm border-b border-zinc-800/60 py-2">
                  <p className="text-zinc-300">{f.overall_rating} · {f.difficulty_rating}{f.movement_confidence ? ` · ${f.movement_confidence}` : ""}</p>
                  {f.difficulty_or_discomfort && <p className="text-xs text-volt mt-0.5">⚠ {f.difficulty_or_discomfort}</p>}
                  {f.additional_comments && <p className="text-xs text-zinc-500 italic mt-0.5">“{f.additional_comments}”</p>}
                </div>
              ))}
            </Section>

            <Section title="Payments" count={profile.payments.length}>
              {profile.payments.map((p) => (
                <div key={p.id} className="flex justify-between text-sm border-b border-zinc-800/60 py-2">
                  <span className="text-zinc-300">{p.package_name} · {p.currency} {p.amount ?? "—"}</span>
                  <span className={`text-[10px] uppercase tracking-[0.12em] font-bold ${p.payment_status === "paid" ? "text-volt" : p.payment_status === "overdue" ? "text-red-400" : "text-zinc-400"}`}>
                    {p.payment_status}
                  </span>
                </div>
              ))}
            </Section>
          </div>
        </div>
      )}
    </div>
  );
}

function Section({ title, count, children }) {
  return (
    <div className="mt-7">
      <p className="text-[10px] uppercase tracking-[0.22em] text-zinc-500 font-bold border-b border-zinc-800 pb-2 mb-1">
        {title} <span className="text-zinc-600">({count})</span>
      </p>
      {count === 0 && <p className="text-xs text-zinc-600 py-2">Nothing yet.</p>}
      {children}
    </div>
  );
}
